import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import PublicNavbar from "@/components/PublicNavbar";
import PublicFooter from "@/components/PublicFooter";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, Heart, Download, Home } from "lucide-react";

export default function DonationResult({ success }) {
  const [sp] = useSearchParams();
  const txnid = sp.get("txnid");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!txnid) { setLoading(false); return; }
    api.get(`/donations/status/${txnid}`).then((r) => setD(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [txnid]);

  const isSuccess = d?.status === "success" || (success && !d);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <PublicNavbar />
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-lg w-full p-10 rounded-3xl border border-slate-200 bg-white text-center shadow-lg fade-in-up">
          {loading ? (
            <><Loader2 className="animate-spin mx-auto" size={40} /><div className="mt-4 text-slate-500">Verifying donation...</div></>
          ) : isSuccess ? (
            <>
              <div className="relative w-20 h-20 mx-auto">
                <div className="absolute inset-0 bg-emerald-100 rounded-full animate-ping" />
                <div className="relative w-20 h-20 rounded-full bg-emerald-500 flex items-center justify-center">
                  <Heart size={38} className="text-white fill-white" />
                </div>
              </div>
              <h1 className="heading text-3xl font-black mt-6">Thank You!</h1>
              <p className="text-slate-600 mt-3">Your donation of <b className="text-brand">₹{d?.amount?.toLocaleString("en-IN") || "—"}</b> to <b>{d?.campaign}</b> has been received.</p>
              {d?.receipt_no && (
                <div className="mt-3 p-2 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-800">
                  Receipt No: <b className="font-mono">{d.receipt_no}</b>
                </div>
              )}
              {txnid && <div className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs font-mono">Txn: {txnid}</div>}
              {d?.receipt_url && (
                <a
                  href={`${(process.env.REACT_APP_BACKEND_URL || '')}${d.receipt_url}`}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-4 inline-flex items-center gap-2 bg-brand hover:bg-[#172b66] text-white font-bold rounded-full px-5 py-2.5 text-sm"
                  data-testid="download-receipt-button"
                >
                  <Download size={14} /> Download 80G Receipt (PDF)
                </a>
              )}
              <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                Your 80G tax-receipt has also been emailed to you as a PDF attachment.<br />
                PMR Welfare Foundation · 80G Reg: AAOCP5008QF20251
              </div>
            </>
          ) : (
            <>
              <XCircle size={64} className="mx-auto text-red-500" />
              <h1 className="heading text-3xl font-black mt-4">Donation Failed</h1>
              <p className="text-slate-600 mt-3">We couldn't process your donation. No amount has been charged.</p>
              {txnid && <div className="mt-5 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs font-mono">Txn: {txnid}</div>}
            </>
          )}
          <div className="mt-6 flex gap-3 justify-center flex-wrap">
            <Link to="/" className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2">
              <Home size={14} /> Home
            </Link>
            {!isSuccess && (
              <Link to="/donate" className="bg-brand hover:bg-[#172b66] text-white font-bold rounded-full px-5 py-2.5 text-sm">Try Again</Link>
            )}
          </div>
        </div>
      </div>
      <PublicFooter contact={null} />
    </div>
  );
}
